import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { Link as RouterLink } from "react-router-dom";

import {
  CheckoutPurchaseItem,
  InstallmentsResponse,
  PaymentMethod,
  PaymentSummaryResponse,
} from "../controller";
import { CartSingleton } from "../../SharedComponents/CartSingleton";
import { AppliedCouponResponse } from "../../clients/answeringClient";
import { trackGtagEvent } from "../../utils/analytics";

type PaymentMethodOption = {
  id: PaymentMethod;
  label: string;
  icon: React.ReactNode;
};

type PurchaseItem = CheckoutPurchaseItem;

interface ReviewPanelProps {
  purchaseItems: PurchaseItem[];
  isCartEmpty: boolean;
  totalPriceFormatted: string;
  selectedMethod?: PaymentMethod | null;
  methods: PaymentMethodOption[];
  renderMethodCard: (
    method: PaymentMethodOption,
    selected: boolean,
    readOnly?: boolean,
    customMessage?: string
  ) => React.ReactNode;
  selectedPaymentMethodLabel: string;
  isSummaryLoading: boolean;
  hasPaymentError: boolean;
  hasStatusError: boolean;
  finalAmountFormatted: string;
  formatCurrency: (value: number) => string;
  subtotalCents: number;
  paymentSummary?: PaymentSummaryResponse;
  paymentSummaryError?: string;
  paymentSummaryLoading: boolean;
  installments?: InstallmentsResponse;
  installmentsError?: string;
  selectedInstallment: number;
  couponDiscountCents?: number;
  progressiveDiscountCents?: number;
  appliedCoupon?: AppliedCouponResponse | null;
}

const ReviewPanel: React.FC<ReviewPanelProps> = ({
  purchaseItems,
  isCartEmpty,
  totalPriceFormatted,
  selectedMethod,
  methods,
  renderMethodCard,
  selectedPaymentMethodLabel,
  isSummaryLoading,
  hasPaymentError,
  hasStatusError,
  finalAmountFormatted,
  formatCurrency,
  subtotalCents,
  paymentSummary,
  paymentSummaryError,
  paymentSummaryLoading,
  installments,
  installmentsError,
  selectedInstallment,
  couponDiscountCents = 0,
  progressiveDiscountCents = 0,
  appliedCoupon,
}) => {
  const appliedCouponCode = appliedCoupon?.code ?? null;
  const [couponInput, setCouponInput] = React.useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = React.useState(false);
  const [couponError, setCouponError] = React.useState<string | undefined>();
  const [couponSuccess, setCouponSuccess] = React.useState<string | undefined>();
  const previousCouponRef = React.useRef<{
    code: string | null | undefined;
    discountCents: number;
  }>({
    code: appliedCouponCode,
    discountCents: couponDiscountCents,
  });

  React.useEffect(() => {
    if (isCartEmpty) {
      setCouponInput("");
      setCouponError(undefined);
      setCouponSuccess(undefined);
    }
  }, [isCartEmpty]);

  React.useEffect(() => {
    const previous = previousCouponRef.current;
    const hasNewCoupon =
      appliedCouponCode &&
      couponDiscountCents > 0 &&
      (appliedCouponCode !== previous.code ||
        couponDiscountCents !== previous.discountCents);

    if (hasNewCoupon) {
      const valueBeforeCents = Math.max(
        subtotalCents - (progressiveDiscountCents ?? 0),
        0
      );
      const discountCents = Math.max(couponDiscountCents, 0);
      const valueAfterCents = Math.max(valueBeforeCents - discountCents, 0);

      const fbq = typeof window !== "undefined" ? window.fbq : undefined;
      if (
        typeof window !== "undefined" &&
        typeof document !== "undefined" &&
        document.cookie.includes("catvanteAnalyticsCookiesAccepted=true") &&
        fbq
      ) {
        fbq("trackCustom", "CouponApplied", {
          coupon: appliedCouponCode,
          discount_value: Number((discountCents / 100).toFixed(2)),
          value_before: Number((valueBeforeCents / 100).toFixed(2)),
          value_after: Number((valueAfterCents / 100).toFixed(2)),
        });
      }

      trackGtagEvent("coupon_applied", {
        coupon: appliedCouponCode,
        discount_value: Number((discountCents / 100).toFixed(2)),
        value_before: Number((valueBeforeCents / 100).toFixed(2)),
        value_after: Number((valueAfterCents / 100).toFixed(2)),
      });
    }

    previousCouponRef.current = {
      code: appliedCouponCode,
      discountCents: couponDiscountCents,
    };
  }, [
    appliedCouponCode,
    couponDiscountCents,
    progressiveDiscountCents,
    subtotalCents,
  ]);

  const handleCouponInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const sanitized = event.target.value.replace(/\s+/g, "").toLowerCase();
    setCouponInput(sanitized);
  };

  const handleApplyCoupon = async () => {
    if (isApplyingCoupon || !couponInput.trim()) {
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError(undefined);
    setCouponSuccess(undefined);

    try {
      await CartSingleton.shared.applyCoupon(couponInput);
      setCouponInput("");
      setCouponSuccess("Cupom aplicado com sucesso.");
    } catch (error: any) {
      setCouponError(
        error?.message ?? "Não foi possível validar o cupom."
      );
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    if (isApplyingCoupon) {
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError(undefined);
    setCouponSuccess(undefined);

    try {
      await CartSingleton.shared.removeCoupon();
      setCouponSuccess("Cupom removido.");
    } catch (error: any) {
      setCouponError(
        error?.message ?? "Não foi possível remover o cupom."
      );
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const renderSummary = () => {
    if (isCartEmpty) {
      return null;
    }

    if (!selectedMethod) {
      return null;
    }

    if (paymentSummaryLoading) {
      return (
        <Box display="flex" alignItems="center" gap={1} marginTop={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Carregando resumo…</Typography>
        </Box>
      );
    }

    if (paymentSummaryError) {
      return (
        <Alert severity="error" sx={{ marginTop: 2 }}>
          {paymentSummaryError}
        </Alert>
      );
    }

    const summary = paymentSummary;
    if (!summary) {
      if (selectedMethod === PaymentMethod.CREDIT_CARD && installmentsError) {
        return (
          <Alert severity="error" sx={{ marginTop: 2 }}>
            {installmentsError}
          </Alert>
        );
      }
      return null;
    }

    const finalAmount = finalAmountFormatted;

    if (selectedMethod === PaymentMethod.BANK_SLIP) {
      const adjustment = summary.method_adjustment_cents || 0;
      return (
        <Box marginTop={1} display="flex" flexDirection="column" gap={1}>
          <Typography variant="subtitle1">Resumo do pagamento</Typography>
          <Typography variant="body2">
            Valor base: {totalPriceFormatted}
          </Typography>
          <Typography variant="body2">
            Taxa de boleto: {formatCurrency(adjustment)}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            Total estimado: {finalAmount}
          </Typography>
        </Box>
      );
    }

    if (selectedMethod === PaymentMethod.CREDIT_CARD) {
      const option = installments?.options.find(
        (item) => item.n === selectedInstallment
      );

      const simulatedSplit =
        option?.value_split_cents != null
          ? formatCurrency(option.value_split_cents)
          : undefined;
      const simulatedTotal =
        option?.value_total_cents != null
          ? formatCurrency(option.value_total_cents)
          : undefined;

      return (
        <Box marginTop={1} display="flex" flexDirection="column" gap={1}>
          <Typography variant="subtitle1">Resumo do parcelamento</Typography>
          <Typography variant="body2">
            Parcelas selecionadas: {selectedInstallment}x
            {simulatedSplit ? ` de ${simulatedSplit}` : ""}
          </Typography>
          {summary.validation?.installments_no_interest && (
            <Typography variant="body2" color="success.main">
              Parcela dentro do limite sem juros da política.
            </Typography>
          )}
          {simulatedTotal && (
            <Typography variant="body2">
              Total estimado: {simulatedTotal}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {summary.notes ??
              "Juros definidos pela operadora do cartão no momento da autorização."}
          </Typography>
        </Box>
      );
    }

    if (selectedMethod === PaymentMethod.PIX) {
      return (
        <Box marginTop={1} display="flex" flexDirection="column" gap={1}>
          <Typography variant="subtitle1">Resumo do pagamento</Typography>
          <Typography variant="body2">
            Valor da compra: {totalPriceFormatted}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            Total estimado: {finalAmount}
          </Typography>
        </Box>
      );
    }

    return null;
  };

  const progressiveDiscountFormatted =
    progressiveDiscountCents > 0
      ? formatCurrency(progressiveDiscountCents)
      : null;
  const couponDiscountFormatted =
    couponDiscountCents > 0 ? formatCurrency(couponDiscountCents) : null;
  const subtotalFormatted = formatCurrency(subtotalCents);

  const priceChangedItems = purchaseItems.filter((item) => item.priceChanged);
  const summaryContent = renderSummary();

  const assignedInstrumentIds =
    appliedCoupon?.assignedInstrumentId ??
    appliedCoupon?.assignedInstrumentIds ??
    null;

  const couponInstrumentNames = React.useMemo(() => {
    if (!assignedInstrumentIds || assignedInstrumentIds.length === 0) {
      return [];
    }
    const idSet = new Set(assignedInstrumentIds);
    return purchaseItems
      .filter((item) => idSet.has(item.id))
      .map((item) => item.title);
  }, [assignedInstrumentIds, purchaseItems]);

  const couponDiscountDetail = React.useMemo(() => {
    if (!assignedInstrumentIds || assignedInstrumentIds.length === 0) {
      return null;
    }

    let discountLabel: string | null = null;
    if (
      appliedCoupon?.discountType === "percent" &&
      appliedCoupon?.percentOff != null
    ) {
      const formattedPercent = new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(appliedCoupon.percentOff);
      discountLabel = `-${formattedPercent}%`;
    } else if (
      appliedCoupon?.discountType === "fixed_amount" &&
      appliedCoupon?.amountOffCents != null
    ) {
      discountLabel = `-${formatCurrency(appliedCoupon.amountOffCents)}`;
    }

    if (!discountLabel) {
      return null;
    }

    const namesToUse =
      couponInstrumentNames.length > 0
        ? couponInstrumentNames
        : assignedInstrumentIds.map((id) => `#${id}`);

    if (namesToUse.length === 0) {
      return discountLabel;
    }

    if (namesToUse.length === 1) {
      return `${discountLabel} no instrumento ${namesToUse[0]}`;
    }

    const instrumentsList = `${namesToUse
      .slice(0, -1)
      .join(", ")} e ${namesToUse[namesToUse.length - 1]}`;
    return `${discountLabel} nos instrumentos ${instrumentsList}`;
  }, [
    appliedCoupon?.amountOffCents,
    appliedCoupon?.discountType,
    appliedCoupon?.percentOff,
    assignedInstrumentIds,
    couponInstrumentNames,
    formatCurrency,
  ]);

  const couponDiscountLabel = couponDiscountDetail
    ? `Desconto do cupom (${couponDiscountDetail})`
    : "Desconto do cupom";

  const renderCouponSection = () => {
    if (isCartEmpty) {
      return null;
    }

    const hasAppliedCoupon = Boolean(appliedCouponCode);

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          border: "1px solid rgba(0, 0, 0, 0.06)",
          backgroundColor: grey[50],
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Cupom de desconto
          </Typography>
          {hasAppliedCoupon ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="column" spacing={1.5}>
                <Chip
                  label={`Cupom aplicado: ${appliedCouponCode ?? ""}`}
                  color="success"
                  sx={{ fontWeight: 500 }}
                />
                {couponDiscountFormatted && (
                  <Typography variant="body2" color="#F19321" fontWeight={600}>
                    Desconto adicional: - {couponDiscountFormatted}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="text"
                  color="error"
                  onClick={handleRemoveCoupon}
                  disabled={isApplyingCoupon}
                >
                  Remover
                </Button>
                {isApplyingCoupon && <CircularProgress size={20} color="inherit" />}
              </Stack>
            </Stack>
          ) : (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <TextField
                label="Código do cupom"
                placeholder="Insira o cupom"
                value={couponInput}
                onChange={handleCouponInputChange}
                onKeyDown={(event) => {
                  if (event.key === " ") {
                    event.preventDefault();
                  }
                }}
                size="small"
                disabled={isApplyingCoupon || isCartEmpty}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyCoupon}
                disabled={
                  isApplyingCoupon || isCartEmpty || couponInput.trim().length === 0
                }
                sx={{
                  borderRadius: 999,
                  px: 3,
                  py: 1,

                }}
              >
                {isApplyingCoupon ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Aplicar"
                )}
              </Button>
            </Stack>
          )}

          {couponError && <Alert severity="error">{couponError}</Alert>}
          {couponSuccess && !couponError && (
            <Alert severity="success">{couponSuccess}</Alert>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Paper variant="outlined" sx={{ padding: { xs: 2, sm: 3 }, borderRadius: 2 }}>
      <Typography variant="h5" fontWeight={700} marginBottom={3}>
        Revisão e pagamento
      </Typography>

      <Box

        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            Carrinho de compras
          </Typography>

          {isCartEmpty ? (
            <Alert severity="info">
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography variant="body2">
                  Seu carrinho está vazio. Volte para a lista de instrumentos e
                  adicione itens para continuar.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/instruments"
                  variant="contained"
                  color="primary"
                  sx={{ alignSelf: "flex-start", borderRadius: 999, px: 3 }}
                >
                  Adicionar itens
                </Button>
              </Box>
            </Alert>
          ) : (
            <>
              {priceChangedItems.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {priceChangedItems.length === 1 ? (
                    <>
                      O preço do instrumento <strong>{priceChangedItems[0].title}</strong> foi atualizado.
                      Confira os valores antes de concluir o pagamento.
                    </>
                  ) : (
                    "Alguns itens tiveram o preço atualizado. Revise os valores antes de concluir o pagamento."
                  )}
                </Alert>
              )}

              <Box display="flex" flexDirection="column" gap={1.5} >
                {purchaseItems.map((item) => {
                  const discountedUnitPrice = formatCurrency(
                    item.discountedUnitPriceCents
                  );
                  const originalUnitPrice = formatCurrency(item.unitPriceCents);
                  const totalPrice = formatCurrency(
                    item.discountedSubtotalCents
                  );
                  const originalTotalPrice = formatCurrency(item.subtotalCents);
                  const previousPrice =
                    item.priceSnapshotCents != null
                      ? formatCurrency(item.priceSnapshotCents)
                      : undefined;
                  const discountPct = item.appliedDiscountPct;
                  const hasDiscount = discountPct != null && discountPct > 0;
                  const formattedDiscountPct = hasDiscount
                    ? new Intl.NumberFormat("pt-BR", {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 0,
                    }).format(discountPct)
                    : null;

                  return (
                    <Paper
                      key={`purchaseItem_${item.id}`}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderColor: "rgba(30,160,151,0.16)",
                        backgroundColor: "grey.50",
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Stack direction="row" gap={1} alignItems="center" flexWrap={'wrap-reverse'}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {item.title}
                          </Typography>
                          {item.priceChanged && (
                            <Chip
                              size="small"
                              color="warning"
                              label="Preço atualizado"
                            />
                          )}
                        </Stack>

                        {/* {item.description && (
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                        )} */}

                        <Stack spacing={0.25}>
                          <Typography variant="body2" color="text.secondary">
                            {item.quantity} x {discountedUnitPrice}
                          </Typography>
                          {hasDiscount && (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textDecoration: "line-through" }}
                              >
                                {item.quantity} x {originalUnitPrice}
                              </Typography>
                              {formattedDiscountPct && (
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label={`-${formattedDiscountPct}%`}
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                            </Stack>
                          )}
                        </Stack>

                        {item.priceChanged && previousPrice && (
                          <Typography variant="caption" color="text.secondary">
                            Preço anterior: {previousPrice}
                          </Typography>
                        )}
                      </Box>

                      <Stack spacing={0.25} alignItems="flex-end">
                        <Typography variant="body1" fontWeight={700} color="primary">
                          {totalPrice}
                        </Typography>
                        {hasDiscount && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textDecoration: "line-through" }}
                          >
                            {originalTotalPrice}
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>

              <Box sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px solid",
                borderColor: grey[300],
              }}>
                {renderCouponSection()}
              </Box>

              <Stack
                spacing={1}
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: grey[300],
                }}
              >
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle1" fontWeight="bold">
                    Subtotal
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {subtotalFormatted}
                  </Typography>
                </Box>
                {couponDiscountFormatted && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {couponDiscountLabel}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color="#F19321"
                    >
                      - {couponDiscountFormatted}
                    </Typography>
                  </Box>
                )}
                {progressiveDiscountFormatted && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography
                      variant="subtitle2"
                      
                      color="text.secondary"
                    >
                      Descontos progressivos
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color="#F19321"
                    >
                      - {progressiveDiscountFormatted}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </>
          )}
        </Box>

        {!isCartEmpty && (
          <>
            <Divider />

            <Box>
              <Typography variant="h6" sx={{ marginBottom: 1 }}>
                Método de pagamento
              </Typography>

              {selectedMethod ? (
                methods
                  .filter((method) => method.id === selectedMethod)
                  .map((method) =>
                    renderMethodCard(
                      method,
                      false,
                      true,
                      method.id === PaymentMethod.CREDIT_CARD
                        ? selectedPaymentMethodLabel
                        : undefined
                    )
                  )
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Selecione um método para ver valores atualizados
                </Typography>
              )}
            </Box>

            {summaryContent && <Box>{summaryContent}</Box>}

            {selectedMethod &&
              !isSummaryLoading &&
              !hasPaymentError &&
              !hasStatusError && (
                <>
                  <Divider />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">
                      Total a pagar
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {finalAmountFormatted}
                    </Typography>
                  </Box>
                </>
              )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default ReviewPanel;
